import { type RechartsLegendPayload as LegendPayload } from '@precis-digital/kurama/recharts';
import { type ThemeColor } from '@precis-digital/kurama/theme';
import type { ElementDescriptionDimension, ElementDescriptionMetric, ElementsDescriptionsMap, LegendFormatter, SecondAxisDetails } from '../../internalTypes';
import type { ChartDataRow, ChartCardDefaults, ChartCardTFunction } from '../../types';
import LegendItem from './LegendItem';
import { formatMetricValue, getLabelOrValueAsLabel, isNullDimensionValue } from '../../utils/generics';
export const getMetricDataKeys = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(secondAxisDetails: Array<SecondAxisDetails<DataKey, DataRow>>): DataKey[] => {
  return secondAxisDetails.reduce<DataKey[]>((acc, detail) => [...acc, ...detail.dataKeys], []);
};
export const fixLegendPayload = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  secondAxisDetails,
  metricDataKeys,
  legendPayload
}: {
  secondAxisDetails: Array<SecondAxisDetails<DataKey, DataRow>>;
  metricDataKeys?: never;
  legendPayload: readonly LegendPayload[] | undefined;
} | {
  secondAxisDetails?: never;
  metricDataKeys: DataKey[];
  legendPayload: readonly LegendPayload[] | undefined;
}): {
  payload: readonly LegendPayload[];
  dataKeys: DataKey[];
  dataKeysTotals: number[];
} | {
  payload?: never;
  dataKeys: DataKey[];
  dataKeysTotals?: never;
} => {
  const dataKeys = metricDataKeys ?? getMetricDataKeys(secondAxisDetails);
  if (legendPayload == null || legendPayload.length === 0) {
    return {
      dataKeys
    };
  }
  const numDataKeys = dataKeys.length;
  const numPayloads = legendPayload.length;
  const segmentCount = Math.floor(numPayloads / numDataKeys);
  const dataKeysTotals = dataKeys.map<number>(dataKey => legendPayload.slice(0, segmentCount).reduce<number>((acc, {
    payload
  }) => acc + ((payload as DataRow)[dataKey] as number ?? 0), 0));
  const fixedPayload = legendPayload?.map(({
    dataKey: _dataKey,
    payload: _payload,
    ...rest
  }, index) => {
    const dataKeyIndex = Math.floor(index / segmentCount);
    const metricDataKey = dataKeys[dataKeyIndex];
    const metricTotal = dataKeysTotals[dataKeyIndex];
    const payload = _payload as DataRow;
    return {
      ...rest,
      payload: {
        ...payload,
        [`${metricDataKey}:percent`]: (payload[metricDataKey] as number ?? 0) / metricTotal
      },
      dataKey: metricDataKey
    };
  });
  return {
    payload: fixedPayload,
    dataKeys,
    dataKeysTotals
  };
};
export const getLegendItemFormatter = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  defaults,
  dimensionKey,
  elementsDescriptionsMap,
  hoveredSlice,
  setRowRef,
  t
}: {
  defaults: ChartCardDefaults;
  dimensionKey: DataKey;
  elementsDescriptionsMap: ElementsDescriptionsMap<DataKey>;
  hoveredSlice?: string | null;
  setRowRef: (id: string) => (el: HTMLTableRowElement | null | undefined) => void;
  t: ChartCardTFunction;
}): LegendFormatter => {
  const formatter: LegendFormatter = (entry, isLastEntry) => {
    const dimensionValue = entry.value as string;
    const metricDataKey = entry.dataKey as DataKey;
    const {
      color
    } = entry;
    const payload = entry.payload as DataRow;
    const value = payload[metricDataKey] as number ?? 0;
    const {
      descriptions
    } = elementsDescriptionsMap[dimensionKey] as ElementDescriptionDimension;
    const dimensionDescription = descriptions?.[dimensionValue];
    const metricDescription = elementsDescriptionsMap[metricDataKey] as ElementDescriptionMetric;
    const formattedValue = formatMetricValue(metricDataKey, metricDescription, value, defaults.axis.format);
    const percentValue = payload[`${metricDataKey}:percent`] as number;
    const isDimmed = hoveredSlice != null && hoveredSlice !== dimensionValue;
    const key = metricDataKey == null ? dimensionValue : `${String(metricDataKey)}-${dimensionValue}`;
    return <LegendItem key={key} color={dimensionDescription?.color ?? color as ThemeColor} label={getLabelOrValueAsLabel(dimensionDescription?.label, dimensionValue)} labelTooltip={isNullDimensionValue(dimensionValue) ? t('noData') : undefined} value={formattedValue} percentage={percentValue} isLastEntry={isLastEntry} isDimmed={isDimmed} rowRef={setRowRef(key)} />;
  };
  return formatter;
};